import {createContext, use, useEffect, useRef} from 'react'
import type {PropsWithChildren} from 'react'
import {createStore, useStore} from 'zustand'
import {devtools} from 'zustand/middleware'
import type {DiffAnchor} from '@github-ui/diffs/types'
import {createDiffLinesSlice, type DiffLinesSlice, type DiffLinesSliceOpts} from './slices/diff-lines'
import {
  createInjectedContextLinesSlice,
  type InjectedContextLinesSlice,
  type InjectedContextlinesSliceOpts,
} from './slices/injected-context-lines'
import {createDialogModeSlice, type DialogModeSlice} from './slices/dialog-mode'
import {createTreeEntriesSlice, type TreeEntriesSlice, type TreeEntriesSliceOpts} from './slices/tree-entries'
import {
  createHiddenFeaturesSlice,
  type HiddenFeaturesSlice,
  type HiddenFeaturesSliceOpts,
} from './slices/hidden-features'
import {areSetsEqual} from '../util/are-sets-equal'
import {
  createMoveNewCommentSlice,
  type MoveNewCommentSlice,
  type MoveNewCommentSliceOpts,
} from './slices/move-new-comment'
import {createStoredCommentSlice, type StoredCommentSlice, type StoredCommentSliceOpts} from './slices/stored-comments'
import {createActionBarVisibilitySlice, type ActionBarVisibilitySlice} from './slices/action-bar-visibility'

type DiffStoreState = DialogModeSlice &
  DiffLinesSlice &
  HiddenFeaturesSlice &
  InjectedContextLinesSlice &
  MoveNewCommentSlice &
  TreeEntriesSlice &
  StoredCommentSlice &
  ActionBarVisibilitySlice

type DiffStore = ReturnType<typeof createDiffStore>

type DiffStoreOpts = {diffAnchor: DiffAnchor; path: string} & DiffLinesSliceOpts &
  HiddenFeaturesSliceOpts &
  InjectedContextlinesSliceOpts &
  MoveNewCommentSliceOpts &
  TreeEntriesSliceOpts &
  StoredCommentSliceOpts

const DiffStoreContext = createContext<DiffStore | null>(null)

export function useDiffStore<T>(selector: (state: DiffStoreState) => T): T {
  const store = use(DiffStoreContext)
  if (!store) throw new Error('Missing DiffStoreProvider in the tree')

  return useStore(store, selector)
}

export const DiffStoreProvider = ({children, ...opts}: PropsWithChildren<DiffStoreOpts>) => {
  const storeRef = useRef<DiffStore | null>(null)
  const prevDiffLinesRef = useRef<DiffStoreOpts['diffLines']>(opts.diffLines)
  const prevDiffViewRef = useRef<DiffStoreOpts['isSplitView']>(opts.isSplitView)
  const prevHiddenFeaturesRef = useRef<DiffStoreOpts['hiddenFeatures']>(opts.hiddenFeatures)

  // eslint-disable-next-line react-hooks/refs
  if (!storeRef.current) {
    storeRef.current = createDiffStore({...opts})
  }

  // Check for changes to diffLines size
  useEffect(() => {
    if (storeRef.current && prevDiffLinesRef.current.length !== opts.diffLines.length) {
      // Updates store's difflines when difflines have changed (e.g. injected context lines added)
      storeRef.current.getState().diffLinesSliceUpdates.updateDiffLinesHash(opts.diffLines)
      // Update the storedCommentsMap when diffLines change as well (for any comments that were on context lines)
      storeRef.current.setState({storedCommentsMap: opts.storedCommentsMap})
      prevDiffLinesRef.current = opts.diffLines
    }
  }, [opts, opts.diffLines])

  // Check for changes to diffView
  useEffect(() => {
    if (storeRef.current && prevDiffViewRef.current !== opts.isSplitView) {
      // Updates store's isSplitView state when toggled between split or unified diff view
      storeRef.current.getState().diffLinesSliceUpdates.updateDiffView(opts.isSplitView)
      prevDiffViewRef.current = opts.isSplitView
    }
  }, [opts, opts.isSplitView])

  // Check for changes to hiddenFeatures
  useEffect(() => {
    if (storeRef.current && !areSetsEqual(prevHiddenFeaturesRef.current, opts.hiddenFeatures)) {
      // Updates store's hiddenFeatures field when hiddenFeatures have changed
      storeRef.current.setState({hiddenFeatures: opts.hiddenFeatures})
      prevHiddenFeaturesRef.current = opts.hiddenFeatures
    }
  }, [opts, opts.hiddenFeatures])

  // eslint-disable-next-line react-hooks/refs
  return <DiffStoreContext value={storeRef.current}>{children}</DiffStoreContext>
}

const createDiffStore = (opts: DiffStoreOpts) =>
  createStore<DiffStoreState>()(
    devtools(
      (...a) => ({
        ...createDialogModeSlice()(...a),
        ...createDiffLinesSlice(opts)(...a),
        ...createHiddenFeaturesSlice(opts)(...a),
        ...createInjectedContextLinesSlice(opts)(...a),
        ...createMoveNewCommentSlice(opts)(...a),
        ...createTreeEntriesSlice(opts)(...a),
        ...createStoredCommentSlice(opts)(...a),
        ...createActionBarVisibilitySlice()(...a),
      }),
      {name: `diff-store-${opts.diffAnchor}`},
    ),
  )

try{ DiffStoreContext.displayName ||= 'DiffStoreContext' } catch {}
try{ DiffStoreProvider.displayName ||= 'DiffStoreProvider' } catch {}